import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'https://api.sm-media.id';
const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [client, setClient] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('sm_token'));
  const [loading, setLoading] = useState(true);
  const tokenRef = useRef(token);
  tokenRef.current = token;

  // Ambil profil terbaru dari server (mis. setelah admin toggle ugc_enabled dari admin panel)
  // dan sinkronkan ke state + localStorage — supaya client tidak perlu logout/login manual
  // tiap kali ada perubahan yang dilakukan admin di sisi server.
  const refreshClient = useCallback(async () => {
    if (!tokenRef.current) return null;
    try {
      const res = await axios.get(`${API}/api/auth/me`, {
        headers: { Authorization: `Bearer ${tokenRef.current}` },
      });
      const c = res.data.client;
      localStorage.setItem('sm_client', JSON.stringify(c));
      setClient(c);
      return c;
    } catch (e) {
      // Token invalid/expired — biarkan PrivateRoute yang urus redirect ke login,
      // jangan paksa logout di sini supaya gangguan jaringan sesaat tidak nge-logout user.
      return null;
    }
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const saved = localStorage.getItem('sm_client');
      if (saved) setClient(JSON.parse(saved));
      refreshClient();
    }
    setLoading(false);
  }, [token, refreshClient]);

  // Refresh begitu tab kembali aktif — kasus paling umum: admin ubah setting sementara
  // client lagi buka tab lain/minimize, terus balik lagi ke tab dashboard.
  useEffect(() => {
    const onFocus = () => { if (tokenRef.current) refreshClient(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) onFocus(); });
    return () => window.removeEventListener('focus', onFocus);
  }, [refreshClient]);

  // Jaring pengaman buat tab yang dibiarkan terbuka lama tanpa pernah pindah tab
  useEffect(() => {
    const interval = setInterval(() => { if (tokenRef.current) refreshClient(); }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [refreshClient]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/api/auth/login`, { email, password });
    const { token: t, client: c } = res.data;
    localStorage.setItem('sm_token', t);
    localStorage.setItem('sm_client', JSON.stringify(c));
    axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
    setToken(t);
    setClient(c);
    return c;
  };

  const logout = async () => {
    await axios.post(`${API}/api/auth/logout`).catch(() => {});
    localStorage.removeItem('sm_token');
    localStorage.removeItem('sm_client');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setClient(null);
  };

  return (
    <AuthContext.Provider value={{ client, token, login, logout, loading, refreshClient, API }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
